import { useState, createContext, useContext, useMemo, FC, useCallback } from 'react'
import {
  QueryState,
  QueryRequestContextProps,
  initialQueryRequest,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import { FiltroIndicadores, ParametrosIndicadores } from './_models'

/**
 * `LocalDate` puro no formato do contrato (AAAA-MM-DD), montado a partir dos
 * componentes locais da data — `toISOString()` deslocaria o dia em America/Sao_Paulo.
 */
const paraDataLocalIso = (data: Date): string => {
  const mes = `${data.getMonth() + 1}`.padStart(2, '0')
  const dia = `${data.getDate()}`.padStart(2, '0')
  return `${data.getFullYear()}-${mes}-${dia}`
}

/** Período inicial: mês corrente (dia 1 até hoje) — período curto, resposta rápida. */
export const montaFiltroInicial = (hoje = new Date()): FiltroIndicadores => ({
  dataInicio: paraDataLocalIso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
  dataFim: paraDataLocalIso(hoje),
})

export const QueryRequestContext =
  createContext<QueryRequestContextProps<FiltroIndicadores>>(initialQueryRequest)

export const QueryRequestProvider: FC<WithChildren> = ({ children }) => {
  const initialState: QueryState<FiltroIndicadores> = {
    ...initialQueryRequest.state,
    params: montaFiltroInicial(),
  }

  const [state, setState] = useState<QueryState<FiltroIndicadores>>(initialState)

  const updateState = useCallback(
    (updates: Partial<QueryState<FiltroIndicadores>>) =>
      setState((prev) => ({
        ...prev,
        ...updates,
      })),
    []
  )

  const value = useMemo(
    () => ({
      state,
      updateState,
    }),
    [state, updateState]
  )

  return <QueryRequestContext.Provider value={value}>{children}</QueryRequestContext.Provider>
}

export const useQueryRequest = () => useContext(QueryRequestContext)

/**
 * Parâmetros de query derivados do filtro, um só para as duas famílias: os cinco
 * parâmetros são idênticos nos dois contratos. Devolve `undefined` enquanto empresa,
 * `dataInicio` ou `dataFim` não estiverem escolhidos — é essa guarda que impede
 * qualquer request antes de o filtro estar completo.
 */
export const useParametrosIndicadores = () => {
  const { state } = useQueryRequest()
  const filtro = state.params

  const parametros = useMemo<ParametrosIndicadores | undefined>(() => {
    if (!filtro?.empresa || !filtro.dataInicio || !filtro.dataFim) return undefined

    return {
      empresa: filtro.empresa.value,
      dataInicio: filtro.dataInicio,
      dataFim: filtro.dataFim,
      idUnidade: filtro.unidade?.value,
      idProfissional: filtro.profissional?.value,
    }
  }, [filtro])

  const query = useMemo(() => (parametros ? JSON.stringify(parametros) : ''), [parametros])

  return { parametros, query }
}
